// src/components/subcontractors/modals/SubcontractorFormModal.jsx
import { useState, useEffect, useRef } from 'react';
import { useSubcontractors } from '../../../context/SubcontractorContext';
import { SPECIALTY_TAGS, AVATAR_COLORS } from '../subcontractorsData';

const RATE_TYPES = [
  { value: 'hourly',      label: 'Per Hour' },
  { value: 'flat',        label: 'Flat Rate' },
  { value: 'per_panel',   label: 'Per Panel' },
  { value: 'per_vehicle', label: 'Per Vehicle' },
];

const STATUS_OPTIONS = ['active', 'inactive', 'blacklisted'];

const EMPTY = {
  name: '', company: '', avatarColor: AVATAR_COLORS[0], avatarUrl: null,
  phone: '', email: '', address: '', website: '', instagram: '',
  specialties: [], defaultRate: { type: 'hourly', amount: '', currency: 'USD' },
  status: 'active', notes: '',
};

function initials(name) {
  return name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

export default function SubcontractorFormModal({ sub, onClose }) {
  const { addSubcontractor, updateSubcontractor } = useSubcontractors();
  const [form, setForm] = useState(() => sub ? {
    ...EMPTY, ...sub,
    // Coerce null optional fields to '' so controlled inputs don't warn
    phone:     sub.phone     ?? '',
    email:     sub.email     ?? '',
    address:   sub.address   ?? '',
    website:   sub.website   ?? '',
    instagram: sub.instagram ?? '',
    notes:     sub.notes     ?? '',
    defaultRate: { ...EMPTY.defaultRate, ...sub.defaultRate },
  } : EMPTY);
  const [errors, setErrors] = useState({});

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: undefined }));
  }

  function setRate(field, value) {
    setForm(f => ({ ...f, defaultRate: { ...f.defaultRate, [field]: value } }));
  }

  function toggleSpecialty(id) {
    setForm(f => ({
      ...f,
      specialties: f.specialties.includes(id)
        ? f.specialties.filter(s => s !== id)
        : [...f.specialties, id],
    }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (form.defaultRate.amount !== '' && isNaN(Number(form.defaultRate.amount)))
      e.amount = 'Must be a number';
    return e;
  }

  function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const data = {
      ...form,
      name:      form.name.trim(),
      company:   form.company.trim(),
      phone:     form.phone.trim() || null,
      email:     form.email.trim() || null,
      address:   form.address.trim() || null,
      website:   form.website.trim() || null,
      instagram: form.instagram.trim() || null,
      notes:     form.notes.trim(),
      defaultRate: {
        ...form.defaultRate,
        amount: form.defaultRate.amount === '' ? 0 : Number(form.defaultRate.amount),
      },
    };
    if (sub) updateSubcontractor(sub.id, data);
    else     addSubcontractor(data);
    onClose();
  }

  // Close on Escape — use a ref so the effect never re-runs due to inline onClose identity
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const inputCls = 'w-full h-8 px-3 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[var(--wm-text-ui)] placeholder:text-[#94A3B8] dark:placeholder:text-[#4A6380] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0]/30 transition-colors';
  const labelCls = 'block text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="bg-white dark:bg-[#111C27] border border-gray-200 dark:border-[#1E3048] rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-[#1E3048]">
          <h2 className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">
            {sub ? 'Edit Subcontractor' : 'New Subcontractor'}
          </h2>
          <button onClick={onClose} className="text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          {/* Avatar preview + color picker */}
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-[var(--wm-text-heading)] flex-shrink-0"
              style={{ background: form.avatarColor }}
            >
              {initials(form.name || 'S')}
            </div>
            <div>
              <p className={labelCls}>Avatar Color</p>
              <div className="flex gap-2 flex-wrap">
                {AVATAR_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => set('avatarColor', c)}
                    className="w-6 h-6 rounded-full transition-transform"
                    style={{
                      background: c,
                      outline: form.avatarColor === c ? `2px solid white` : 'none',
                      outlineOffset: '2px',
                      transform: form.avatarColor === c ? 'scale(1.15)' : 'scale(1)',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Name + Company */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Name <span className="text-red-400">*</span></label>
              <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jose Barajas" />
              {errors.name && <p className="text-[var(--wm-text-ui)] text-red-400 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className={labelCls}>Company</label>
              <input className={inputCls} value={form.company} onChange={e => set('company', e.target.value)} placeholder="Barajas Pro Wraps" />
            </div>
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Phone</label>
              <input className={inputCls} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(512) 555-0000" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input className={inputCls} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="name@example.com" />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className={labelCls}>Address</label>
            <input className={inputCls} value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St, Austin TX" />
          </div>

          {/* Website + Instagram */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Website</label>
              <input className={inputCls} value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://example.com" />
            </div>
            <div>
              <label className={labelCls}>Instagram</label>
              <input className={inputCls} value={form.instagram} onChange={e => set('instagram', e.target.value)} placeholder="@handle" />
            </div>
          </div>

          {/* Specialties */}
          <div>
            <label className={labelCls}>Specialties</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALTY_TAGS.map(tag => {
                const selected = form.specialties.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    onClick={() => toggleSpecialty(tag.id)}
                    className="px-3 py-1 rounded-full text-[var(--wm-text-ui)] font-medium border transition-colors"
                    style={{
                      background:   selected ? tag.color + '33' : 'transparent',
                      borderColor:  selected ? tag.color : 'var(--wm-nav-border)',
                      color:        selected ? tag.color : 'var(--wm-nav-text)',
                    }}
                  >
                    {tag.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Default Rate */}
          <div>
            <label className={labelCls}>Default Rate</label>
            <div className="flex gap-2">
              <select
                className={inputCls + ' flex-1'}
                value={form.defaultRate.type}
                onChange={e => setRate('type', e.target.value)}
              >
                {RATE_TYPES.map(r => <option key={r.value} value={r.value} className="bg-[#0F1923] text-[#F8FAFE]">{r.label}</option>)}
              </select>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] dark:text-[#7D93AE] text-[var(--wm-text-body)]">$</span>
                <input
                  className={inputCls + ' pl-6'}
                  type="number"
                  min="0"
                  value={form.defaultRate.amount}
                  onChange={e => setRate('amount', e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            {errors.amount && <p className="text-[var(--wm-text-ui)] text-red-400 mt-1">{errors.amount}</p>}
          </div>

          {/* Status */}
          <div>
            <label className={labelCls}>Status</label>
            <div className="flex gap-2">
              {STATUS_OPTIONS.map(s => (
                <button
                  key={s}
                  onClick={() => set('status', s)}
                  className="flex-1 py-1.5 rounded-lg text-[var(--wm-text-ui)] font-medium capitalize border transition-colors"
                  style={{
                    background:  form.status === s ? (s === 'active' ? '#10B98122' : s === 'blacklisted' ? '#EF444422' : '#F59E0B22') : 'transparent',
                    borderColor: form.status === s ? (s === 'active' ? '#10B981'   : s === 'blacklisted' ? '#EF4444'   : '#F59E0B')   : 'var(--wm-nav-border)',
                    color:       form.status === s ? (s === 'active' ? '#10B981'   : s === 'blacklisted' ? '#EF4444'   : '#F59E0B')   : 'var(--wm-nav-text)',
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className={labelCls}>Notes</label>
            <textarea
              className={inputCls + ' !h-auto py-2 resize-y'}
              rows={3}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Any notes about this subcontractor..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-[#1E3048]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 bg-[#2E8BF0] hover:bg-[#1a7de0] text-white text-[var(--wm-text-body)] font-medium rounded-lg transition-colors"
          >
            {sub ? 'Save Changes' : 'Add Subcontractor'}
          </button>
        </div>
      </div>
    </div>
  );
}
